import { Chip } from "@base/ui/Chip";
import { Radio } from "@base/ui/Radio";
import { mdiCheck } from "@mdi/js";
import Icon from "@mdi/react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { PLANS } from "../model/constants";
import type { ApplyFormInstance } from "../model/useApplyForm";

type Props = {
  form: ApplyFormInstance;
};

export const PlanSelector = ({ form }: Props) => {
  const { t } = useTranslation();

  return (
    <form.Field name="plan">
      {(field) => {
        const selectedPlanType = field.state.value.split("-")[0];

        return (
          <div className="overflow-x-hidden">
            <span className="inline-block mb-3 text-label2">
              {t("apply.plan.label")} <span className="text-[14px] text-red-500 font-medium leading-none">*</span>
            </span>

            <Radio
              value={field.state.value || undefined}
              onValueChange={(details) => {
                field.handleChange(details.value ?? "");
              }}
            >
              <div className="flex overflow-x-auto gap-4 scrollbar-hide">
                {PLANS.map((plan) => (
                  <div
                    key={plan.name}
                    className={cn(
                      "flex flex-col bg-white border rounded-8 min-w-[240px] flex-1",
                      selectedPlanType === plan.apiType
                        ? "border-[3px] border-primary-500 p-4"
                        : "border-gray-300 p-[18px]",
                    )}
                  >
                    <div className="flex gap-2 items-center">
                      <h3 className="text-gray-1000 text-2xl leading-[1.3] tracking-[-0.48px] font-bold">
                        {plan.name}
                      </h3>
                    </div>

                    <div className="flex relative flex-col gap-4 mt-4">
                      {plan.price.map((price) => (
                        <Radio.Item
                          key={price.amount}
                          value={`${plan.apiType}-${
                            price.unit === "year" ? "YEARLY" : price.unit === "month" ? "MONTHLY" : "DAILY"
                          }`}
                          label={
                            price.isDiscounted ? (
                              <span className="inline-flex gap-1 items-center">
                                {`$${price.amount.toLocaleString()} / ${t(`apply.plan.priceUnit.${price.unit}`)}`}
                                <Chip radius="square" color="error">
                                  {t("apply.plan.discount")}
                                </Chip>
                              </span>
                            ) : (
                              `$${price.amount.toLocaleString()} / ${t(`apply.plan.priceUnit.${price.unit}`)}`
                            )
                          }
                        />
                      ))}
                    </div>

                    <div className="my-4 w-full h-px bg-gray-200" />

                    <ul className="flex flex-col gap-3">
                      {plan.features.map((feature) => (
                        <li key={feature.text} className="flex gap-2 items-start">
                          <Icon path={mdiCheck} size="20px" className="flex-shrink-0 text-gray-900" />
                          <span
                            className={cn(
                              "text-gray-900 text-sm leading-[1.5] tracking-[-0.28px]",
                              feature.isBold && "font-bold",
                            )}
                          >
                            {t(feature.text)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Radio>
          </div>
        );
      }}
    </form.Field>
  );
};
